import { useState } from "react";
import { toast } from "sonner";
import { Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FormInput } from "@/components/shared/FormInput";
import { PrimaryButton } from "@/components/shared/Buttons";
import { submitContactMessage, isValidIndianMobile, isValidEmail } from "@/lib/api";

// Reusable "Request Callback" dialog — posts to /api/contact-messages.
export const RequestCallbackDialog = ({ trigger, programName = "", testId = "request-callback" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!isValidIndianMobile(form.mobile)) next.mobile = "Enter a valid 10-digit Indian mobile number.";
    if (!isValidEmail(form.email)) next.email = "Enter a valid email address.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSubmitting(true);
    try {
      await submitContactMessage({
        name: form.name,
        mobile: form.mobile,
        email: form.email,
        subject: programName ? `Callback request – ${programName}` : "Callback request",
        message: programName
          ? `Please call me back regarding the ${programName}.`
          : "Please call me back regarding ASAI programs.",
      });
      setForm({ name: "", mobile: "", email: "" });
      setOpen(false);
      toast.success("Thanks! Our team will call you back shortly.");
    } catch (err) {
      setErrors(err.errors || {});
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild data-testid={`${testId}-trigger`}>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-navy">Request a Callback</DialogTitle>
          <DialogDescription>
            {programName ? `Interested in ${programName}? ` : ""}Share your details and our team will call you back.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} noValidate className="mt-2 flex flex-col gap-4">
          <FormInput label="Full name" id="cb-name" value={form.name} onChange={update("name")} error={errors.name} placeholder="Your name" testId={`${testId}-name`} />
          <FormInput label="Mobile number" id="cb-phone" value={form.mobile} onChange={update("mobile")} error={errors.mobile} placeholder="10-digit mobile" testId={`${testId}-phone`} />
          <FormInput label="Email" id="cb-email" type="email" value={form.email} onChange={update("email")} error={errors.email} placeholder="you@example.com" testId={`${testId}-email`} />
          <PrimaryButton type="submit" size="lg" disabled={submitting} testId={`${testId}-submit`}>
            {submitting ? "Sending…" : (<><Phone className="h-4 w-4" /> Request Callback</>)}
          </PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};
